import { Group, Select } from "@mantine/core";
import { RichTextEditor } from "@mantine/tiptap";
import type { Editor } from "@tiptap/react";
import {
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft,
  IconAlignRight,
} from "@tabler/icons-react";
import { useCallback } from "react";
import { ADDENDUM_FONT_SIZES } from "./clinicalRichTextExtensions";

interface AddendumTypographyControlsProps {
  editor: Editor | null;
}

export function AddendumTypographyControls({
  editor,
}: AddendumTypographyControlsProps) {
  const handleFontSizeChange = useCallback(
    (value: string | null) => {
      if (!editor) return;
      const chain = editor.chain().focus();
      if (value) chain.setFontSize(value).run();
      else chain.unsetFontSize().run();
    },
    [editor]
  );
  const handleAlignLeft = useCallback(() => {
    editor?.chain().focus().setTextAlign("left").run();
  }, [editor]);
  const handleAlignCenter = useCallback(() => {
    editor?.chain().focus().setTextAlign("center").run();
  }, [editor]);
  const handleAlignRight = useCallback(() => {
    editor?.chain().focus().setTextAlign("right").run();
  }, [editor]);
  const handleAlignJustify = useCallback(() => {
    editor?.chain().focus().setTextAlign("justify").run();
  }, [editor]);

  return (
    <Group gap={4} wrap="nowrap">
      <Select
        aria-label="Font size"
        placeholder="Default size"
        data={ADDENDUM_FONT_SIZES.map((size) => ({ value: size, label: size }))}
        clearable
        value={editor?.getAttributes("textStyle").fontSize || null}
        onChange={handleFontSizeChange}
        w={130}
      />
      <RichTextEditor.Control
        aria-label="Align left"
        title="Align left"
        active={editor?.isActive({ textAlign: "left" })}
        onClick={handleAlignLeft}
      >
        <IconAlignLeft size={16} />
      </RichTextEditor.Control>
      <RichTextEditor.Control
        aria-label="Align center"
        title="Align center"
        active={editor?.isActive({ textAlign: "center" })}
        onClick={handleAlignCenter}
      >
        <IconAlignCenter size={16} />
      </RichTextEditor.Control>
      <RichTextEditor.Control
        aria-label="Align right"
        title="Align right"
        active={editor?.isActive({ textAlign: "right" })}
        onClick={handleAlignRight}
      >
        <IconAlignRight size={16} />
      </RichTextEditor.Control>
      <RichTextEditor.Control
        aria-label="Justify"
        title="Justify"
        active={editor?.isActive({ textAlign: "justify" })}
        onClick={handleAlignJustify}
      >
        <IconAlignJustified size={16} />
      </RichTextEditor.Control>
    </Group>
  );
}
